/**
 * We want to limit imports directly from `launchdarkly-react-client-sdk` to prevent
 * untyped feature flag usage
 */
// eslint-disable-next-line no-restricted-imports
import { useFlags as useLaunchDarklyFlags } from 'launchdarkly-react-client-sdk';
import { type z } from 'zod';

import { type FeatureFlagKey, FeatureFlagSchemas } from './schemas';

type FlagSchemas = typeof FeatureFlagSchemas;
export type InferredFlagType<TFlagKey extends FeatureFlagKey> = z.infer<FlagSchemas[TFlagKey]>;

export type LaunchDarklyFlags = {
  [K in FeatureFlagKey]: InferredFlagType<K>;
};

interface UseFlagProps<TFlagKey extends FeatureFlagKey> {
  flagKey: TFlagKey;
}

export function useFlag<TFlagKey extends FeatureFlagKey>(
  props: UseFlagProps<TFlagKey>
): InferredFlagType<TFlagKey> {
  const { flagKey } = props;
  const flags = useLaunchDarklyFlags();
  const schema = FeatureFlagSchemas[flagKey];

  const result = schema.safeParse(flags[flagKey]);
  if (result.error) {
    console.error(
      `Flag validation failed for ${flagKey}. Check zod schema for default.`,
      result.error
    );
    return schema.parse(undefined) as InferredFlagType<TFlagKey>;
  }
  return result.data as InferredFlagType<TFlagKey>;
}

/**
 * Do not use `useFlags` on most pages. This is only allowed for nav bar and home page where we want to use multiple flags.
 */
export function useFlags(): LaunchDarklyFlags {
  const flags = useLaunchDarklyFlags();
  return (Object.keys(FeatureFlagSchemas) as FeatureFlagKey[]).reduce<LaunchDarklyFlags>(
    (acc, key) => {
      const flagKey = key;
      const schema = FeatureFlagSchemas[flagKey];
      const result = schema.safeParse(flags[flagKey]);
      if (result.error) {
        console.error(
          `Flag validation failed for ${flagKey}. Check zod schema for default.`,
          result.error
        );
        const defaultValue = schema.parse(undefined);
        (acc as any)[flagKey] = defaultValue;
      } else {
        (acc as any)[flagKey] = result.data;
      }
      return acc;
    },
    {} as LaunchDarklyFlags
  );
}
